import { DASHBOARD_ROUTES } from "../dashboardRoutes";
import { SETTINGS_ROUTES } from "../../settingProfile/settingsRoutes";
import React, { useMemo, useState, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import Swal from 'sweetalert2';
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import MyWalletSidebar from "../MyWalletSidebar";
import HeaderProfileSection from "../HeaderProfileSection";
import AnswerDoubt, { Composer } from "./AnswerDoubt";
import {
  useDoubtActions,
  useDoubtDetail,
  useDoubtReportReasons,
} from "../../hooks/useDoubts";
import { useSidebarData } from "../../hooks/useSidebarData";
import { buildDoubtFlowState } from "./doubtFlowState";
import { DOUBT_ROUTES } from "./doubtRoutes";
import { getStoredUser } from "../../utils/authStorage";
import profileImage from "../../assets/images/c1.png";
import usePreferredLanguageToggle from "../../hooks/usePreferredLanguageToggle";
import LogoutModal from "../../settingProfile/LogoutPopup";
import {
  AnswerIcon,
  DoubtShareIcon,
  DoubtUpvoteIcon,
} from "../../components/SvgIcons";
import "../../dashboard/Dashboard.css";
import "../../DashboardQuestionBank.css";
import "../HomeDashboard.css";
import "../questionBank/QbankDetail.css";
import "./DoubtTabs.css";
import "./DoubtDetail.css";
import AddDoubtModal from './modals/AddDoubt';
import BlockDoubtModal from './modals/BlockDoubt';
import ReportModal from './modals/DoubtReport';

const safeHtml = (value) => ({
  __html: DOMPurify.sanitize(String(value || "")),
});
const firstArray = (...values) => values.find(Array.isArray) || [];

const DoubtDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const storedUser = getStoredUser();
  const flow = useMemo(
    () => buildDoubtFlowState(location.state),
    [location.state],
  );
  const doubtId = Number(flow?.doubt_id || flow?.selectedDoubt?.id || 0);
  const language = String(
    flow?.language ||
      storedUser?.content_language ||
      storedUser?.language ||
      "EN",
  ).toUpperCase();
  const detail = useDoubtDetail(doubtId, language);
  const actions = useDoubtActions(doubtId, language);
  const reasonsQuery = useDoubtReportReasons(language, Boolean(doubtId));
  const [addDoubtModalOpen, setAddDoubtModalOpen] = useState(false);
  const [isBlockDoubtOpen, setIsBlockDoubtOpen] = useState(false);
  const [isDoubtReportOpen, setIsDoubtReportOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const doubt = detail.data;
  const userId = Number(storedUser?.user_id || storedUser?.id || 0);
  const isOwner = Boolean(
    doubt && (doubt.canEdit || (userId && userId === doubt.ownerUserId)),
  );

  // Note: Report reasons were fetched here but custom ReportModal is currently static.

  const { data: sidebarData } = useSidebarData();
  const profile = sidebarData?.user_data || storedUser || {};
  const {
    currentPreferredLanguage,
    handleLanguageToggle,
    isPending: isLanguageUpdatePending,
  } = usePreferredLanguageToggle(profile);
  const profileName = profile?.name || profile?.user_name || "Student";
  const profileAvatar = profile?.user_profile || profile?.profile_image || profile?.image || profile?.user_image || profile?.avatar || profileImage;
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const profileMenuRef = useRef(null);

  const handleProfileMenuNavigate = (path) => {
    setIsProfileOpen(false);
    navigate(path);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goBack = () =>
    flow?.qbankBackTarget?.pathname
      ? navigate(flow.qbankBackTarget.pathname, {
          state: flow.qbankBackTarget.state,
          replace: true,
        })
      : navigate(-1);
  const share = async () => {
    const url = `${window.location.origin}${DOUBT_ROUTES.detail}?doubt_id=${doubtId}&state_id=${Number(flow?.state_id || 0)}&category_id=${Number(flow?.category_id || 0)}`;
    try {
      if (navigator.share)
        await navigator.share({ title: doubt?.title || "Doubt", url });
      else if (navigator.clipboard) await navigator.clipboard.writeText(url);
    } catch (error) {
      if (error?.name !== "AbortError")
        console.error("Unable to share doubt.", error);
    }
  };

  if (!doubtId)
    return (
      <HomeDashboardLayout showHeader={false} variant="doubt-detail-layout">
        <div className="doubt-detail-status">No doubt was selected.</div>
        <MyWalletSidebar withTopOffset={false} />
      </HomeDashboardLayout>
    );

  return (
    <>
      <HomeDashboardLayout showHeader={false} variant="doubt-detail-layout">
        <div
          className="categories-page qbank-detail-page doubt-detail"
          style={{ background: "#fff" }}
        >
          <div className="categories-page-header">
            <button
              type="button"
              className="categories-back-button"
              onClick={goBack}
              aria-label="Go back"
            >
              <i className="bi bi-chevron-left" />
            </button>
            <h1 className="categories-page-title">Doubt Detail</h1>
            <div className="qb-top" aria-label="Doubt actions">
              <button
                className={`qb-lang${currentPreferredLanguage === "HI" ? " is-hi" : ""}${isLanguageUpdatePending ? " is-loading" : ""}`}
                type="button"
                aria-label={`Switch language to ${currentPreferredLanguage === "HI" ? "English" : "Hindi"}`}
                title={`Current language: ${currentPreferredLanguage}. Click to switch to ${currentPreferredLanguage === "HI" ? "English" : "Hindi"}`}
                onClick={handleLanguageToggle}
                disabled={isLanguageUpdatePending}
              >
                <i className="bi bi-globe" aria-hidden="true" />
                <span className="qb-lang__badge">{currentPreferredLanguage}</span>
              </button>

              <button
                className="qb-notify"
                type="button"
                aria-label="Notifications"
                onClick={() => navigate(SETTINGS_ROUTES.notifications)}
              >
                <i className="bi bi-bell" aria-hidden="true" />
                <span className="qb-notify-badge">8</span>
              </button>

              <div
                className={`qb-profileWrap ${isProfileOpen ? "is-open" : ""}`}
                ref={profileMenuRef}
              >
                <button
                  className="qb-profile"
                  type="button"
                  aria-label="Student profile"
                  aria-expanded={isProfileOpen}
                  aria-haspopup="menu"
                  onClick={() => setIsProfileOpen((current) => !current)}
                >
                  <img
                    src={profileAvatar}
                    alt={profileName}
                    className="qb-profile-img"
                  />
                  <span className="qb-profile-name">{`Hi, ${profileName}`}</span>
                  <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                </button>

                {isProfileOpen && (
                  <div
                    className="qb-profileMenu"
                    role="menu"
                    aria-label="Student profile menu"
                  >
                    <button
                      type="button"
                      className="qb-profileMenu__item"
                      role="menuitem"
                      onClick={() =>
                        handleProfileMenuNavigate(SETTINGS_ROUTES.profile)
                      }
                    >
                      My Profile
                    </button>
                    <button
                      type="button"
                      className="qb-profileMenu__item"
                      role="menuitem"
                      onClick={() =>
                        handleProfileMenuNavigate(DASHBOARD_ROUTES.home)
                      }
                    >
                      My Progress
                    </button>
                    <button
                      type="button"
                      className="qb-profileMenu__item"
                      role="menuitem"
                      onClick={() =>
                        handleProfileMenuNavigate(SETTINGS_ROUTES.password)
                      }
                    >
                      Settings
                    </button>
                    <button
                      type="button"
                      className="qb-profileMenu__item qb-profileMenu__item--danger"
                      role="menuitem"
                      onClick={() => {
                        setIsProfileOpen(false);
                        setShowLogoutModal(true);
                      }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="qb-Detail-content">
            <div className="d-flex qbank-detail-main">
              <section
                className="qbank-LeftContent"
                aria-label="Doubt and answers"
              >
                {detail.isLoading ? (
                  <div className="doubt-detail-shimmer">
                    <span />
                    <span />
                    <span />
                    <span />
                  </div>
                ) : null}
                {detail.isError ? (
                  <div className="doubt-detail-status doubt-detail-status--error">
                    {detail.error?.message || "Unable to load this doubt."}
                  </div>
                ) : null}
                {doubt ? (
                  <div className="qbank-card qbank-card--question">
                    <article className="question-card">
                      <div className="question-card__header">
                        <div className="question-card__user">
                          <div className="question-card__avatar">
                            <img
                              src={doubt.authorImage || profileImage}
                              alt=""
                            />
                          </div>
                          <div className="question-card__user-info">
                            <div className="question-card__user-name">
                              {doubt.authorName}
                            </div>
                            <div className="question-card__time">
                              {doubt.createdAt}
                            </div>
                          </div>
                        </div>
                        <div className="question-card__header-actions">
                          <button
                            type="button"
                            className={
                              doubt.bookmarkStatus ? "is-bookmarked" : ""
                            }
                            onClick={() => actions.bookmarkDoubt.mutate()}
                            aria-label="Toggle bookmark"
                          >
                            <i
                              className={`bi ${doubt.bookmarkStatus ? "bi-bookmark-fill" : "bi-bookmark"}`}
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => setMenuOpen((value) => !value)}
                            aria-label="More options"
                          >
                            <i className="bi bi-three-dots-vertical" />
                          </button>
                          {menuOpen ? (
                            <div className="doubt-detail-menu">
                              {isOwner ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMenuOpen(false);
                                      navigate(DOUBT_ROUTES.ask, {
                                        state: {
                                          ...flow,
                                          selectedDoubt: doubt?.raw,
                                          qbankBackTarget: {
                                            pathname: DOUBT_ROUTES.detail,
                                            state: flow,
                                          },
                                        },
                                      });
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      Swal.fire({
                                        icon: 'info',
                                        title: 'Unavailable',
                                        text: 'Doubt deletion is currently unavailable.',
                                      })
                                    }
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => { setMenuOpen(false); setIsDoubtReportOpen(true); }}
                                  >
                                    Report
                                  </button>
                                  <button type="button" onClick={() => { setMenuOpen(false); setIsBlockDoubtOpen(true); }}>
                                    Block
                                  </button>
                                  <button type="button" onClick={() => { setMenuOpen(false); setAddDoubtModalOpen(true); }}>
                                    {doubt.isAddedToMyDoubts
                                      ? "Remove from Your Doubts"
                                      : "Add to Your Doubts"}
                                  </button>
                                </>
                              )}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {doubt.subjectName ? (
                        <div className="question-card__module">
                          {doubt.subjectName}
                        </div>
                      ) : null}
                      {doubt.topicNames?.length ? (
                        <div className="doubt-card__topics">
                          {doubt.topicNames.join(" • ")}
                        </div>
                      ) : null}
                      <div
                        className="question-card__title"
                        dangerouslySetInnerHTML={safeHtml(doubt.title)}
                      />
                      {doubt.sourceModule ? (
                        <div className="question-card__source">
                          Source Module: <span>{doubt.sourceModule}</span>
                        </div>
                      ) : null}
                      <div className="question-card__detail">
                        <div
                          dangerouslySetInnerHTML={safeHtml(doubt.description)}
                        />
                      </div>
                      {doubt.images.length ? (
                        <div className="doubt-detail-attachments">
                          {doubt.images.map((image, index) => (
                            <button
                              type="button"
                              key={`${image}-${index}`}
                              onClick={() => setSelectedImage(image)}
                            >
                              <img src={image} alt="Doubt attachment" />
                            </button>
                          ))}
                        </div>
                      ) : null}
                      <div className="question-card__footer">
                        <button
                          type="button"
                          className={doubt.isUpvoted ? "is-upvoted" : ""}
                          onClick={() => actions.upvoteDoubt.mutate()}
                        >
                          <DoubtUpvoteIcon />
                          <span>{doubt.upvoteCount} Upvote</span>
                        </button>
                        <button type="button">
                          <AnswerIcon />
                          <span>
                            {doubt.answerCount} Answer
                            {doubt.answerCount === 1 ? "" : "s"}
                          </span>
                        </button>
                        <button type="button" onClick={share}>
                          <DoubtShareIcon />
                          <span>Share</span>
                        </button>
                      </div>
                    </article>
                    <Composer
                      placeholder="Add an answer"
                      submitLabel="Post answer"
                      isPending={actions.answer.isPending}
                      onSubmit={(payload) =>
                        actions.answer.mutateAsync(payload)
                      }
                    />
                  </div>
                ) : null}
              </section>
              <aside className="qbank-rightContent" aria-label="Answers">
                {doubt ? (
                  <AnswerDoubt
                    answers={doubt.answers}
                    actions={actions}
                    canMarkBest={isOwner}
                  />
                ) : null}
              </aside>
            </div>
          </div>
        </div>
        <MyWalletSidebar withTopOffset={false} />
      </HomeDashboardLayout>
      {selectedImage ? (
        <div
          className="image-viewer-overlay"
          onClick={() => setSelectedImage("")}
        >
          <div className="image-viewer-content">
            <img
              src={selectedImage}
              alt="Doubt attachment preview"
              className="image-viewer-image"
            />
          </div>
        </div>
      ) : null}
      <AddDoubtModal
        open={addDoubtModalOpen}
        onClose={() => setAddDoubtModalOpen(false)}
        doubtId={doubtId}
        language={language}
        isAdded={doubt?.isAddedToMyDoubts}
      />
      <BlockDoubtModal
        open={isBlockDoubtOpen}
        onClose={() => setIsBlockDoubtOpen(false)}
        authorName={doubt?.authorName}
        doubtId={doubtId}
        language={language}
      />
      <ReportModal
        open={isDoubtReportOpen}
        onClose={() => setIsDoubtReportOpen(false)}
        doubtId={doubtId}
        language={language}
      />

      <LogoutModal
        open={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
      />
    </>
  );
};

export default DoubtDetail;
